import * as React from 'react';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'accent' | 'outline';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  rounded?: boolean;
}

const variantStyles: Record<string, React.CSSProperties> = {
  primary: {
    background: 'linear-gradient(180deg, #06B6D4 0%, #0891B2 100%)',
    color: 'var(--text-inverse)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 4px 12px rgba(6,182,212,0.2), inset 0 1px 0 rgba(255,255,255,0.15)',
  },
  secondary: {
    background: 'var(--bg-primary)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border-primary)',
    boxShadow: 'var(--shadow-xs)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--text-tertiary)',
  },
  danger: {
    background: 'linear-gradient(180deg, #EF4444 0%, #DC2626 100%)',
    color: 'var(--text-inverse)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 4px 12px rgba(239,68,68,0.2)',
  },
  success: {
    background: 'linear-gradient(180deg, #10B981 0%, #059669 100%)',
    color: 'var(--text-inverse)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 4px 12px rgba(16,185,129,0.2)',
  },
  accent: {
    background: 'linear-gradient(180deg, #F59E0B 0%, #D97706 100%)',
    color: 'var(--text-inverse)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 4px 12px rgba(245,158,11,0.2)',
  },
  outline: {
    background: 'transparent',
    color: 'var(--accent-primary)',
    border: '2px solid var(--accent-primary)',
  },
};

const variantHoverStyles: Record<string, React.CSSProperties> = {
  primary: {
    background: 'linear-gradient(180deg, #0891B2 0%, #0E7490 100%)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.12), 0 8px 20px rgba(6,182,212,0.35), inset 0 1px 0 rgba(255,255,255,0.15)',
  },
  secondary: {
    background: 'var(--bg-secondary)',
    border: '1px solid #D1D5DB',
    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
  },
  ghost: {
    background: 'var(--hover-overlay)',
    color: 'var(--text-secondary)',
  },
  danger: {
    background: 'linear-gradient(180deg, #DC2626 0%, #B91C1C 100%)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.12), 0 8px 20px rgba(239,68,68,0.35)',
  },
  success: {
    background: 'linear-gradient(180deg, #059669 0%, #047857 100%)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.12), 0 8px 20px rgba(16,185,129,0.35)',
  },
  accent: {
    background: 'linear-gradient(180deg, #D97706 0%, #B45309 100%)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.12), 0 8px 20px rgba(245,158,11,0.35)',
  },
  outline: {
    background: 'rgba(6,182,212,0.08)',
    border: '2px solid var(--accent-primary-hover)',
  },
};

const variantActiveStyles: Record<string, React.CSSProperties> = {
  primary: {
    background: 'linear-gradient(180deg, #0E7490 0%, #155E75 100%)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1), 0 2px 6px rgba(6,182,212,0.15), inset 0 2px 4px rgba(0,0,0,0.1)',
  },
  secondary: {
    background: 'var(--bg-tertiary)',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)',
  },
  ghost: {
    background: 'var(--active-overlay)',
  },
  danger: {
    background: 'linear-gradient(180deg, #B91C1C 0%, #991B1B 100%)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1), inset 0 2px 4px rgba(0,0,0,0.1)',
  },
  success: {
    background: 'linear-gradient(180deg, #047857 0%, #065F46 100%)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1), inset 0 2px 4px rgba(0,0,0,0.1)',
  },
  accent: {
    background: 'linear-gradient(180deg, #B45309 0%, #92400E 100%)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1), inset 0 2px 4px rgba(0,0,0,0.1)',
  },
  outline: {
    background: 'rgba(6,182,212,0.15)',
  },
};

const sizeStyles: Record<string, React.CSSProperties> = {
  sm: {
    padding: '8px 16px',
    fontSize: '13px',
    minHeight: '36px',
    borderRadius: 'var(--radius-md)',
    gap: '6px',
  },
  md: {
    padding: '12px 20px',
    fontSize: '14px',
    minHeight: '44px',
    borderRadius: 'var(--radius-md)',
    gap: '8px',
  },
  lg: {
    padding: '16px 28px',
    fontSize: '16px',
    minHeight: '52px',
    borderRadius: 'var(--radius-lg)',
    gap: '10px',
  },
  icon: {
    padding: '10px',
    minWidth: '44px',
    minHeight: '44px',
    borderRadius: 'var(--radius-md)',
  },
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      rounded = false,
      disabled,
      children,
      style,
      onMouseEnter,
      onMouseLeave,
      onMouseDown,
      onMouseUp,
      ...props
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const [isPressed, setIsPressed] = React.useState(false);
    const isDisabled = disabled || isLoading;

    const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!isDisabled) setIsHovered(true);
      onMouseEnter?.(e);
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
      setIsHovered(false);
      setIsPressed(false);
      onMouseLeave?.(e);
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!isDisabled) setIsPressed(true);
      onMouseDown?.(e);
    };

    const handleMouseUp = (e: React.MouseEvent<HTMLButtonElement>) => {
      setIsPressed(false);
      onMouseUp?.(e);
    };

    const baseStyle: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      whiteSpace: 'nowrap',
      fontWeight: 600,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      border: 'none',
      transition: 'all var(--motion-duration-fast) var(--motion-ease-standard)',
      opacity: isDisabled ? 0.5 : 1,
      width: fullWidth ? '100%' : 'auto',
      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      position: 'relative',
      overflow: 'hidden',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      transform: isPressed && !isDisabled ? 'scale(0.97)' : isHovered && !isDisabled ? 'translateY(-1px)' : 'none',
      ...variantStyles[variant],
      ...sizeStyles[size],
      ...(isHovered && !isDisabled && !isPressed ? variantHoverStyles[variant] : {}),
      ...(isPressed && !isDisabled ? variantActiveStyles[variant] : {}),
      ...(rounded ? { borderRadius: 'var(--radius-full)' } : {}),
      ...style,
    };

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        style={baseStyle}
        className={className}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        {...props}
      >
        {/* Shimmer effect on hover for primary buttons */}
        {variant === 'primary' && isHovered && !isDisabled && (
          <span
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
              transform: 'translateX(-100%)',
              animation: 'shimmer 0.6s ease forwards',
              pointerEvents: 'none',
            }}
          />
        )}

        {isLoading ? (
          <Loader2
            style={{
              width: size === 'sm' ? '14px' : size === 'lg' ? '20px' : '16px',
              height: size === 'sm' ? '14px' : size === 'lg' ? '20px' : '16px',
              animation: 'spin 0.8s linear infinite'
            }}
          />
        ) : leftIcon ? (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              transition: 'transform var(--motion-duration-fast) var(--motion-ease-standard)',
              transform: isHovered ? 'scale(1.1)' : 'scale(1)',
            }}
          >
            {leftIcon}
          </span>
        ) : null}

        <span style={{ position: 'relative', zIndex: 1 }}>{children}</span>

        {!isLoading && rightIcon && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              transition: 'transform var(--motion-duration-fast) var(--motion-ease-standard)',
              transform: isHovered ? 'translateX(2px)' : 'translateX(0)',
            }}
          >
            {rightIcon}
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
